import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import useApi from '../hooks/useApi';
import toast from 'react-hot-toast';

const FilePreviewModal = ({ file, isOpen, onClose, initialPage }) => {
  const { get } = useApi();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      loadFile();
    }
    
    return () => {
      // Cleanup blob URL when modal closes or file changes
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [isOpen, file]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if it's an image or PDF
      const isPreviewable = file.mimeType?.startsWith('image/') || file.mimeType === 'application/pdf';
      
      if (!isPreviewable) {
        setError('This file type cannot be previewed directly.');
        setLoading(false);
        return;
      }

      const response = await get(`/files/${file.fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response]));
      setBlobUrl(url);
    } catch (err) {
      console.error('Failed to load file preview:', err);
      setError('Failed to load file preview. You may not have permission to view this file.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
  };

  const handleWatermarkDownload = async () => {
    try {
      if (!file?.id) {
        toast.error('Missing file id for watermark');
        return;
      }
      const watermarked = await get(`/pdf/watermark/${file.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(watermarked);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `watermarked_${file.originalName || file.fileName || 'document.pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download watermarked PDF:', err);
      toast.error('Failed to download watermarked PDF');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col relative animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-800 truncate max-w-md" title={file?.originalName}>
              {file?.originalName}
            </h3>
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
              {file?.mimeType}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {file?.mimeType === 'application/pdf' && (
              <button 
                onClick={handleWatermarkDownload}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
                title="Download with Watermark"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Watermark</span>
              </button>
            )}
            {blobUrl && (
              <button 
                onClick={handleDownload}
                className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-100 relative overflow-hidden flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
              <p>Loading preview...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-red-500 p-8 text-center">
              <AlertTriangle className="w-12 h-12" />
              <p className="font-medium">{error}</p>
              <button 
                onClick={handleDownload} // Try download anyway if preview fails
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download Instead
              </button>
            </div>
          ) : (
            <>
              {file.mimeType === 'application/pdf' ? (
                <iframe 
                  src={initialPage ? `${blobUrl}#page=${initialPage}` : blobUrl} 
                  className="w-full h-full border-none" 
                  title="PDF Preview"
                />
              ) : file.mimeType?.startsWith('image/') ? (
                <img 
                  src={blobUrl} 
                  alt={file.originalName} 
                  className="max-w-full max-h-full object-contain shadow-lg" 
                />
              ) : (
                <div className="text-center p-8">
                  <p className="text-gray-500 mb-4">Preview not available for this file type.</p>
                  <button 
                    onClick={handleDownload}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                  >
                    Download File
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
